import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, OptionCard, Textarea } from '@/components/ui/Form';
import { Helper } from '@/components/ui/Surface';
import type { QuestionSchema } from '@/api/miniapp/types';
import { cleanQuestionTitle, questionParts } from '@/lib/titles';
import { isEmptyAnswerChosen, isOtherSelected, type QuestionDraft } from './questionRules';

export { draftOf, hasSavedAnswer, isAnswered, isRequired, isSkippedAnswer, type QuestionDraft } from './questionRules';

/**
 * Один вопрос анкеты. Тип поля задаёт бэкенд.
 *
 * Схема снята с живого сервера, поэтому типы перечислены явно, а не угаданы по
 * названию: `text`, `textarea`, `phone`, `single_select`, `multi_select`,
 * `group` (несколько полей под одним вопросом) и `list` (список, который
 * пользователь дополняет сам). Незнакомый тип рисуется текстовым полем — так
 * ответ не теряется, даже если бэкенд добавит новый вид вопроса.
 */

interface Props {
  question: QuestionSchema;
  draft: QuestionDraft;
  onChange: (draft: QuestionDraft) => void;
}

export function QuestionField({ question, draft, onChange }: Props) {
  const set = (patch: Partial<QuestionDraft>) => onChange({ ...draft, ...patch });

  const selectedList = Array.isArray(draft.value) ? draft.value : [];

  // Предел `max` из схемы во множественном выборе не применяется: подсказка
  // обещает «несколько вариантов», а после второго остальные гасли
  const toggleOption = (option: string) => {
    const next = selectedList.includes(option)
      ? selectedList.filter((item) => item !== option)
      : [...selectedList, option];
    set({ value: next });
  };

  return (
    <>
      {renderControl()}

      {question.other && isOtherSelected(question, draft.value) ? (
        <div className="mt-3">
          <Input
            label={question.other.title}
            value={draft.otherValue}
            onChange={(event) => set({ otherValue: event.target.value })}
          />
        </div>
      ) : null}

      {question.comment ? (
        <div className="mt-3">
          <Textarea
            label={question.comment.title}
            value={draft.comment}
            onChange={(event) => set({ comment: event.target.value })}
          />
        </div>
      ) : null}
    </>
  );

  function renderControl() {
    switch (question.type) {
      case 'textarea':
        return (
          <Textarea
            placeholder={question.placeholder}
            value={typeof draft.value === 'string' ? draft.value : ''}
            onChange={(event) => set({ value: event.target.value })}
          />
        );

      case 'phone':
        return (
          <Input
            type="tel"
            inputMode="tel"
            placeholder="+7 900 000-00-00"
            value={typeof draft.value === 'string' ? draft.value : ''}
            onChange={(event) => set({ value: event.target.value })}
          />
        );

      case 'single_select':
        return (
          <>
            {(question.options ?? []).map((option) => (
              <OptionCard
                key={option}
                title={option}
                selected={draft.value === option}
                onSelect={() => set({ value: option })}
              />
            ))}
          </>
        );

      case 'multi_select':
        return (
          <>
            {(question.options ?? []).map((option) => {
              const selected = selectedList.includes(option);
              return (
                <OptionCard
                  key={option}
                  control="checkbox"
                  title={option}
                  selected={selected}
                  onSelect={() => toggleOption(option)}
                />
              );
            })}
            <Helper>Можно выбрать несколько вариантов</Helper>
          </>
        );

      case 'group':
        return (
          <>
            {(question.fields ?? []).map((field) => {
              const record = (draft.value ?? {}) as Record<string, string | null>;
              return (
                <div key={field.key} className="mb-3">
                  <Input
                    label={field.label}
                    placeholder={field.placeholder}
                    value={record[field.key] ?? ''}
                    onChange={(event) =>
                      set({ value: { ...record, [field.key]: event.target.value } })
                    }
                  />
                </div>
              );
            })}
          </>
        );

      case 'list':
        return (
          <ListControl
            question={question}
            items={selectedList}
            emptyChosen={isEmptyAnswerChosen(question, draft.value)}
            onChange={(items) => set({ value: items })}
          />
        );

      case 'text':
      default:
        return (
          <Input
            placeholder={question.placeholder}
            value={typeof draft.value === 'string' ? draft.value : ''}
            onChange={(event) => set({ value: event.target.value })}
          />
        );
    }
  }
}

/** Список строк с добавлением и удалением строк. */
function ListControl({
  question,
  items,
  emptyChosen,
  onChange,
}: {
  question: QuestionSchema;
  items: string[];
  emptyChosen: boolean;
  onChange: (items: string[]) => void;
}) {
  // Одна пустая строка показывается всегда: иначе непонятно, куда писать
  const rows = items.length > 0 ? items : [''];
  const canAdd = !question.max || rows.length < question.max;
  const placeholder =
    question.placeholder &&
    cleanQuestionTitle(question.placeholder) !== questionParts(question).title
      ? question.placeholder
      : undefined;

  return (
    <>
      {rows.map((item, index) => (
        <div key={index} className="mb-2 flex items-start gap-2">
          {/* Номер строки нужен, когда строк несколько: одна строка с «1.»
              читается как нумерация вопроса */}
          {rows.length > 1 ? (
            <span className="mt-3 w-4 shrink-0 text-[12px] tabular-nums text-muted" aria-hidden>
              {index + 1}.
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <Input
              // Пример из схемы показывает, в каком виде ждут ответ. Повтор
              // формулировки вопроса примером не является и дублирует заголовок
              placeholder={index === 0 ? placeholder : undefined}
              value={item}
              onChange={(event) => {
                const next = [...rows];
                next[index] = event.target.value;
                onChange(next);
              }}
            />
          </div>
          {rows.length > 1 ? (
            <button
              type="button"
              aria-label="Удалить строку"
              onClick={() => onChange(rows.filter((_, position) => position !== index))}
              className="bd-focus mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-control border border-line text-muted"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </div>
      ))}

      {canAdd ? (
        <Button
          className="mt-1 min-h-[40px] text-[12px]"
          icon={<Plus className="h-4 w-4" aria-hidden />}
          onClick={() => onChange([...rows, ''])}
        >
          {question.addButtonText ?? 'Добавить'}
        </Button>
      ) : null}

      {question.allowEmptyAnswer && question.emptyAnswerText ? (
        <OptionCard
          control="checkbox"
          title={question.emptyAnswerText}
          // Отмечен только явным выбором: пустой список сам по себе значит,
          // что на вопрос ещё не ответили
          selected={emptyChosen}
          // Повторное нажатие снимает выбор: пустая строка — это «ещё не
          // ответил», а не «нечего указать»
          onSelect={() => onChange(emptyChosen ? [''] : [])}
        />
      ) : null}
    </>
  );
}
